import {
  Add,
  KeyboardArrowDown,
  KeyboardArrowUp,
  MoreVert,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Collapse,
  createFilterOptions,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { useConfirm } from "material-ui-confirm";
import { useEffect, useState } from "react";
import { QuestionnaireItemProps } from ".";
import { CodeableConcept } from "../../../../../types/codeableConcept";
import { createCodeableConcept } from "../../../../../utils/DataUtils";
import AutocompleteFreeOptions from "../../../../../components/form/AutocompleteFreeOptions";

const pregnancyProcedures = [
  { code: "LA33555-6", display: "Dilation and Curettage (D&C)" },
  { code: "LA28212-1", display: "Hysterectomy" },
  { code: "LA16248-9", display: "Tubal Ligation" },
  { code: "LA20042-0", display: "Lower Segment Cesarean Section (LSCS)" },
];

type PregnancyData = {
  pregnancyDate: Date | null;
  term: number | null;
  preterm: number | null;
  termination: number | null;
  living?: string;
  sex?: string;
  deliveryMethod?: string;
  outcome?: string;
  complications?: string;
  procedures?: CodeableConcept;
};

type PregnancyHistoryTableFormProps = {
  mode?: "edit" | "add";
  initial?: PregnancyData;
  onSubmit: (data: PregnancyData, isEdit: boolean) => void;
  onClose: () => void;
};

const initialPregnancyData: PregnancyData = {
  pregnancyDate: null,
  term: null,
  preterm: null,
  termination: null,
  living: "",
  sex: "",
  deliveryMethod: "",
  outcome: "",
  complications: "",
  procedures: null,
};

const filter = createFilterOptions<{ code: string; display: string }>();

const PregnancyHistoryTableForm = ({
  mode,
  initial,
  onSubmit,
  onClose,
}: PregnancyHistoryTableFormProps) => {
  const [pregnancyData, setPregnancyData] = useState(
    initial || initialPregnancyData,
  );

  useEffect(() => {
    if (initial) {
      setPregnancyData(initial);
    }
  }, [initial]);

  const isValid = () => !!pregnancyData?.pregnancyDate;

  return (
    <Paper sx={{ p: 2 }}>
      <Grid container spacing={1}>
        <Grid item xs={12} md={12} pb={1}>
          <Typography
            variant="body1"
            sx={{ fontWeight: "semibold", fontSize: "1rem" }}
          >
            {mode === "edit" ? "Edit" : "New"} Pregnancy History
          </Typography>
        </Grid>
        <Grid item xs={12} md={3}>
          <DatePicker
            renderInput={(props) => (
              <TextField
                fullWidth
                {...props}
                InputLabelProps={{ shrink: true }}
                helperText={null}
                required
              />
            )}
            label="Date"
            value={pregnancyData?.pregnancyDate || null}
            onChange={(date) =>
              setPregnancyData({
                ...pregnancyData,
                pregnancyDate: date,
              })
            }
            inputFormat="MM/yyyy"
            views={["month", "year"]}
            disableMaskedInput={false}
            mask="__/____"
          />
        </Grid>

        <Grid item xs={12} md={3}>
          <TextField
            id="term-input"
            value={pregnancyData.term || ""}
            onChange={(e) => {
              const value = e.target.value ? parseFloat(e.target.value) : null;
              setPregnancyData({
                ...pregnancyData,
                term: value,
              });
            }}
            label="Term"
            placeholder="Enter weeks"
            type="number"
            size="small"
          />
        </Grid>

        <Grid item xs={12} md={3}>
          <TextField
            id="preterm-input"
            value={pregnancyData.preterm || ""}
            onChange={(e) => {
              const value = e.target.value ? parseFloat(e.target.value) : null;
              setPregnancyData({
                ...pregnancyData,
                preterm: value,
              });
            }}
            label="Preterm"
            placeholder="Enter weeks"
            type="number"
            size="small"
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            id="termination-input"
            value={pregnancyData.termination || ""}
            onChange={(e) => {
              const value = e.target.value ? parseFloat(e.target.value) : null;
              setPregnancyData({
                ...pregnancyData,
                termination: value,
              });
            }}
            label="Termination"
            placeholder="Enter weeks"
            type="number"
            size="small"
          />
        </Grid>

        <Grid item xs={12} md={2}>
          <FormControl fullWidth>
            <InputLabel id="pregnancy-living-select-label">Living</InputLabel>
            <Select
              labelId="pregnancy-living-select-label"
              id="pregnancy-living-select"
              value={pregnancyData.living || ""}
              label="Living"
              onChange={(e) =>
                setPregnancyData({
                  ...pregnancyData,
                  living: e.target.value,
                })
              }
            >
              <MenuItem value="">None</MenuItem>
              <MenuItem value="Yes">Yes</MenuItem>
              <MenuItem value="No">No</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={3}>
          <FormControl fullWidth>
            <InputLabel id="pregnancy-sex-select-label">Sex</InputLabel>
            <Select
              labelId="pregnancy-sex-select-label"
              id="pregnancy-sex-select"
              value={pregnancyData.sex || ""}
              label="Sex"
              onChange={(e) =>
                setPregnancyData({
                  ...pregnancyData,
                  sex: e.target.value,
                })
              }
            >
              <MenuItem value="">None</MenuItem>
              <MenuItem value="Male">Male</MenuItem>
              <MenuItem value="Female">Female</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={4}>
          <FormControl fullWidth>
            <InputLabel id="pregnancy-delivery-method-select-label">
              Delivery Method
            </InputLabel>
            <Select
              labelId="pregnancy-delivery-method-select-label"
              id="pregnancy-delivery-method-select"
              value={pregnancyData.deliveryMethod || ""}
              label="Delivery Method"
              onChange={(e) =>
                setPregnancyData({
                  ...pregnancyData,
                  deliveryMethod: e.target.value,
                })
              }
            >
              <MenuItem value="">None</MenuItem>
              <MenuItem value="Spontaneous Vaginal Delivery (SVD)">
                Spontaneous Vaginal Delivery (SVD)
              </MenuItem>
              <MenuItem value="Vaginal Birth after Cesarean Delivery (VBAC)">
                Vaginal Birth after Cesarean Delivery (VBAC)
              </MenuItem>
              <MenuItem value="Cesarean Section (C-Section)">
                Cesarean Section (C-Section)
              </MenuItem>
              <MenuItem value="Assisted Vaginal Delivery with Forceps">
                Assisted Vaginal Delivery with Forceps
              </MenuItem>
              <MenuItem value="Assisted Vaginal Delivery with Vacuum">
                Assisted Vaginal Delivery with Vacuum
              </MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={3}>
          <FormControl fullWidth>
            <InputLabel id="pregnancy-outcome-select-label">Outcome</InputLabel>
            <Select
              labelId="pregnancy-outcome-select-label"
              id="pregnancy-outcome-select"
              value={pregnancyData.outcome || ""}
              label="Outcome"
              onChange={(e) =>
                setPregnancyData({
                  ...pregnancyData,
                  outcome: e.target.value,
                })
              }
            >
              <MenuItem value="">None</MenuItem>
              <MenuItem value="Live Birth">Live Birth</MenuItem>
              <MenuItem value="Stillbirth">Stillbirth</MenuItem>
              <MenuItem value="Miscarriage">Miscarriage</MenuItem>
              <MenuItem value="Ectopic">Ectopic</MenuItem>
              <MenuItem value="Termination">Termination</MenuItem>
              <MenuItem value="Vesicular Mole">Vesicular Mole</MenuItem>
              <MenuItem value="Early Neonatal Death (EUD)">
                Early Neonatal Death (EUD)
              </MenuItem>
              <MenuItem value="Late Neonatal Death (LUD)">
                Late Neonatal Death (LUD)
              </MenuItem>
              <MenuItem value="Infant Death">Infant Death</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Complications"
            name="complications"
            value={pregnancyData.complications}
            onChange={(e) =>
              setPregnancyData({
                ...pregnancyData,
                complications: e.target.value,
              })
            }
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <AutocompleteFreeOptions
            type="pregnancy-procedures"
            value={
              pregnancyData.procedures
                ? {
                    code: pregnancyData.procedures?.coding.at(0)?.code,
                    display: pregnancyData.procedures?.text,
                  }
                : null
            }
            options={pregnancyProcedures}
            getOptionLabel={(o) => {
              if (typeof o === "string") return o;
              return o?.display ?? "";
            }}
            isOptionEqualToValue={(o, v) => o?.code === v?.code}
            size="small"
            textFieldProps={{
              label: "Procedures",
            }}
            filterOptions={(options, params) => {
              const filtered = filter(options, params);
              const { inputValue } = params;
              const isExisting = options.some(
                (option) => option.display === inputValue,
              );
              if (inputValue !== "" && !isExisting) {
                filtered.push({
                  display: `Add "${inputValue}"`,
                  code: inputValue,
                  isCreatable: true,
                } as {
                  display: string;
                  code: string;
                });
              }
              return filtered;
            }}
            onChange={(e, value) => {
              if (typeof value === "string") return;
              const code = value?.code
                ? createCodeableConcept(
                    "http://snomed.info/sct",
                    value.code,
                    value.display,
                  )
                : null;
              setPregnancyData({
                ...pregnancyData,
                procedures: code,
              });
            }}
          />
        </Grid>

        <Grid item xs={12} md={12}>
          <Button
            variant="contained"
            color="primary"
            sx={{ mr: 2 }}
            disabled={!isValid()}
            onClick={() => onSubmit(pregnancyData as PregnancyData, !!initial)}
          >
            {initial ? "Update" : "Save"}
          </Button>
          <Button variant="outlined" onClick={onClose} color="inherit">
            Close
          </Button>
        </Grid>
      </Grid>
    </Paper>
  );
};

const PregnancyHistoryTableRow = ({
  row,
  onEdit,
  onDelete,
  disabled,
}: {
  row: PregnancyData;
  onEdit: () => void;
  onDelete: () => void;
  disabled?: boolean;
}) => {
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const openMenu = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const confirmDelete = useConfirm();

  const handleDelete = () => {
    confirmDelete({
      title: "Remove Pregnancy Record",
      description: `Are you sure you want to remove this pregnancy record, dated: ${dayjs(row.pregnancyDate).format("MM/YYYY")}?`,
    }).then(() => {
      onDelete();
    });
  };

  return (
    <>
      <TableRow sx={{ "& > *": { borderBottom: "unset" } }}>
        <TableCell>
          <Stack direction="row">
            <IconButton
              aria-label="expand row"
              size="small"
              onClick={() => setOpen(!open)}
            >
              {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
            </IconButton>
            {!disabled && (
              <Box>
                <IconButton
                  aria-label="more-options"
                  id="more-button"
                  size="small"
                  aria-haspopup="true"
                  onClick={handleClick}
                >
                  <MoreVert />
                </IconButton>
                <Menu
                  id="more-menu"
                  MenuListProps={{
                    "aria-labelledby": "more-button",
                  }}
                  anchorEl={anchorEl}
                  open={openMenu}
                  onClose={() => setAnchorEl(null)}
                >
                  <MenuItem onClick={onEdit}>
                    <Typography component="span" color="primary">
                      Edit
                    </Typography>
                  </MenuItem>
                  <MenuItem color="error" onClick={handleDelete}>
                    <Typography component="span" color="error">
                      Remove
                    </Typography>
                  </MenuItem>
                </Menu>
              </Box>
            )}
          </Stack>
        </TableCell>
        <TableCell component="th" scope="row">
          {dayjs(row.pregnancyDate).format("MM/YYYY")}
        </TableCell>
        <TableCell>{row.term || "-"}</TableCell>
        <TableCell>{row.preterm || "-"}</TableCell>
        <TableCell>{row.termination || "-"}</TableCell>
        <TableCell>{row.living || "-"}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ my: 1 }}>
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <Stack direction="column" spacing={1}>
                    <Stack direction="row" gap={1}>
                      <Typography variant="body2" fontWeight="medium">
                        Sex:
                      </Typography>
                      <Typography variant="body2">{row.sex || "-"}</Typography>
                    </Stack>

                    <Stack direction="row" gap={1}>
                      <Typography variant="body2" fontWeight="medium">
                        Outcome:
                      </Typography>
                      <Typography variant="body2">
                        {row.outcome || "-"}
                      </Typography>
                    </Stack>

                    <Stack direction="row" gap={1}>
                      <Typography variant="body2" fontWeight="medium">
                        Procedures:
                      </Typography>
                      <Typography variant="body2">
                        {row.procedures?.coding?.[0]?.display || "-"}
                      </Typography>
                    </Stack>
                  </Stack>
                </Grid>
                <Grid item xs={6}>
                  <Stack direction="column" spacing={1}>
                    <Stack direction="row" gap={1}>
                      <Typography variant="body2" fontWeight="medium">
                        Delivery Method:
                      </Typography>
                      <Typography variant="body2">
                        {row.deliveryMethod || "-"}
                      </Typography>
                    </Stack>

                    <Stack direction="row" gap={1}>
                      <Typography variant="body2" fontWeight="medium">
                        Complications:
                      </Typography>
                      <Typography variant="body2">
                        {row.complications || "-"}
                      </Typography>
                    </Stack>
                  </Stack>
                </Grid>
              </Grid>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

export default function PregnancyDataTable({
  value,
  item,
  onChange,
  disabled,
}: QuestionnaireItemProps) {
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState<PregnancyData>();
  const [editIndex, setEditIndex] = useState<number>();
  // Consider removing this state and using the value prop directly, if causing issues
  const [pregnancyData, setPregnancyData] = useState<PregnancyData[]>(
    value?.value || [],
  );

  const onNewRecord = () => {
    setShowForm(true);
    setFormData(undefined);
    setEditIndex(undefined);
  };

  const onEditRecord = (index: number) => {
    setShowForm(true);
    setFormData(pregnancyData[index]);
    setEditIndex(index);
  };

  const handleSubmit = (data: PregnancyData, isEdit: boolean) => {
    let newData = [];
    if (isEdit) {
      newData = [...pregnancyData];
      newData[editIndex] = data;
      setPregnancyData(newData);
    } else {
      newData = [...pregnancyData, data];
      setPregnancyData(newData);
    }

    setShowForm(false);
    setFormData(undefined);
    setEditIndex(undefined);

    if (onChange) {
      onChange(item, { answerObj: { value: newData } });
    }
  };

  const onDelete = (index: number) => {
    const newData = [...pregnancyData];
    newData.splice(index, 1);
    setPregnancyData(newData);

    if (onChange) {
      onChange(item, { answerObj: { value: newData } });
    }
  };

  return (
    <Box mt={2} width="100%">
      <Typography
        variant="h6"
        pl={1}
        sx={{ fontWeight: "semibold", fontSize: "1rem" }}
      >
        Past Pregnancy History
      </Typography>
      <TableContainer>
        <Table sx={{ minWidth: "100%" }} aria-label="simple table">
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell>
                Date{" "}
                <Typography component="span" variant="caption">
                  (MM/YYYY)
                </Typography>
              </TableCell>
              <TableCell>
                Term{" "}
                <Typography component="span" variant="caption">
                  (weeks)
                </Typography>
              </TableCell>
              <TableCell>
                Preterm{" "}
                <Typography component="span" variant="caption">
                  (weeks)
                </Typography>
              </TableCell>
              <TableCell>
                Termination{" "}
                <Typography component="span" variant="caption">
                  (weeks)
                </Typography>
              </TableCell>
              <TableCell>Living</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pregnancyData.length > 0 ? (
              pregnancyData.map((row, index) => (
                <PregnancyHistoryTableRow
                  key={String(row.pregnancyDate)}
                  row={row}
                  onEdit={() => onEditRecord(index)}
                  onDelete={() => onDelete(index)}
                  disabled={disabled}
                />
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  No data
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Box mt={1}>
        {!showForm && !disabled && (
          <Button
            variant="outlined"
            color="primary"
            startIcon={<Add />}
            size="small"
            onClick={() => onNewRecord()}
          >
            Add Record
          </Button>
        )}

        {showForm && (
          <PregnancyHistoryTableForm
            mode={formData ? "edit" : "add"}
            initial={formData}
            onSubmit={handleSubmit}
            onClose={() => setShowForm(false)}
          />
        )}
      </Box>
    </Box>
  );
}
